import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const PersonalLoanEMICalculatorScreen = () => {
  const colors = {
    primary: '#4F46E5',
    background: '#FFFFFF',
    surface: '#F8FAFC',
    cardBackground: '#FFFFFF',
    border: '#E2E8F0',
    text: '#1E293B',
    textSecondary: '#64748B',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
  };
  const [loanAmount, setLoanAmount] = useState('500000');
  const [interestRate, setInterestRate] = useState(12.5);
  const [loanTenure, setLoanTenure] = useState(3);
  const [tenureType, setTenureType] = useState('years');
  const [processingFee, setProcessingFee] = useState(2);
  const [processingFeeType, setProcessingFeeType] = useState('percentage');
  const [monthlyIncome, setMonthlyIncome] = useState('50000');
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAffordability, setShowAffordability] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);

  const tenureOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  const processingFeeOptions = [
    { label: '%', value: 'percentage' },
    { label: '₹', value: 'fixed' },
  ];

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    if (!num) return '';
    const cleanNum = num.toString().replace(/[^0-9]/g, '');
    return cleanNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleLoanAmountChange = (text) => {
    const cleanText = text.replace(/[^0-9]/g, '');
    setLoanAmount(cleanText);
  };

  const handleMonthlyIncomeChange = (text) => {
    const cleanText = text.replace(/[^0-9]/g, '');
    setMonthlyIncome(cleanText);
  };

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) || 0;
    const annualRate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;
    
    if (principal <= 0 || annualRate <= 0 || tenure <= 0) {
      setResults(null);
      return;
    }

    const monthlyRate = annualRate / 100 / 12;
    const tenureInMonths = tenureType === 'years' ? tenure * 12 : tenure;
    
    // EMI calculation using formula: P * r * (1+r)^n / ((1+r)^n - 1)
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureInMonths)) / 
                (Math.pow(1 + monthlyRate, tenureInMonths) - 1);
    
    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    // Calculate processing fee
    let processingFeeAmount = 0;
    if (processingFeeType === 'percentage') {
      processingFeeAmount = (principal * parseFloat(processingFee)) / 100;
    } else {
      processingFeeAmount = parseFloat(processingFee) || 0;
    }

    const totalCost = totalAmount + processingFeeAmount;

    // Generate year-wise breakdown for amortization schedule
    const breakdown = [];
    let remainingPrincipal = principal;
    
    for (let year = 1; year <= Math.ceil(tenureInMonths / 12); year++) {
      const startMonth = (year - 1) * 12 + 1;
      const endMonth = Math.min(year * 12, tenureInMonths);
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthlyInterest = remainingPrincipal * monthlyRate;
        const monthlyPrincipal = emi - monthlyInterest;
        
        yearlyInterest += monthlyInterest;
        yearlyPrincipal += monthlyPrincipal;
        remainingPrincipal -= monthlyPrincipal;
      }
      
      breakdown.push({
        year,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest),
        balance: Math.max(0, Math.round(remainingPrincipal)),
      });
    }

    setResults({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      principal: Math.round(principal),
      processingFee: Math.round(processingFeeAmount),
      totalCost: Math.round(totalCost),
      breakdown,
      tenureInMonths,
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTenure, tenureType, processingFee, processingFeeType]);

  const loadMoreRows = () => {
    setDisplayedRows(prev => prev + 5);
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>Personal Loan EMI Calculator</Text>
        <Text style={styles.headerSubtitle}>
          Calculate your personal loan EMI, processing fees, and total cost with detailed payment schedule
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.sectionTitle}>Loan Details</Text>
      
      {/* Loan Amount Input */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Loan amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={formatNumber(loanAmount)}
            onChangeText={handleLoanAmountChange}
            placeholder="5,00,000"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
        </View>
      </View>

      {/* Preset Loan Amount Buttons */}
      <View style={styles.presetContainer}>
        <Text style={styles.presetLabel}>Quick Select:</Text>
        <View style={styles.presetButtons}>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('200000')}
          >
            <Text style={styles.presetButtonText}>₹2L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('500000')}
          >
            <Text style={styles.presetButtonText}>₹5L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('1000000')}
          >
            <Text style={styles.presetButtonText}>₹10L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('2000000')}
          >
            <Text style={styles.presetButtonText}>₹20L</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Interest Rate Slider */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Interest rate (per annum)</Text>
          <Text style={styles.sliderValue}>{interestRate}%</Text>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={8}
          maximumValue={30}
          value={interestRate}
          onValueChange={setInterestRate}
          step={0.1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>8%</Text>
          <Text style={styles.sliderLabel}>30%</Text>
        </View>
      </View>

      {/* Loan Tenure */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Loan tenure</Text>
          <Text style={styles.sliderValue}>{loanTenure} {tenureType}</Text>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={tenureType === 'years' ? 1 : 6}
          maximumValue={tenureType === 'years' ? 7 : 84}
          value={loanTenure}
          onValueChange={setLoanTenure}
          step={tenureType === 'years' ? 1 : 6}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>
            {tenureType === 'years' ? '1 year' : '6 months'}
          </Text>
          <Text style={styles.sliderLabel}>
            {tenureType === 'years' ? '7 years' : '84 months'}
          </Text>
        </View>
      </View>

      {/* Tenure Type Toggle */}
      <View style={styles.toggleContainer}>
        {tenureOptions.map((option) => (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.toggleButton,
              tenureType === option.value && styles.toggleButtonActive,
            ]}
            onPress={() => {
              setTenureType(option.value);
              if (option.value === 'years') {
                setLoanTenure(Math.min(Math.max(Math.round(loanTenure / 12), 1), 7));
              } else {
                setLoanTenure(Math.min(Math.max(loanTenure * 12, 6), 84));
              }
            }}
          >
            <Text
              style={[
                styles.toggleButtonText,
                tenureType === option.value && styles.toggleButtonTextActive,
              ]}
            >
              {option.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Processing Fee */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Processing fee</Text>
        <View style={styles.processingFeeContainer}>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.textInput}
              value={processingFee.toString()}
              onChangeText={(text) => setProcessingFee(text.replace(/[^0-9.]/g, ''))}
              placeholder="2"
              keyboardType="numeric"
              placeholderTextColor={colors.textSecondary}
            />
          </View>
          <View style={styles.toggleContainer}>
            {processingFeeOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.toggleButton,
                  processingFeeType === option.value && styles.toggleButtonActive,
                ]}
                onPress={() => setProcessingFeeType(option.value)}
              >
                <Text
                  style={[
                    styles.toggleButtonText,
                    processingFeeType === option.value && styles.toggleButtonTextActive,
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Affordability Check */}
      <TouchableOpacity
        style={styles.affordabilityToggle}
        onPress={() => setShowAffordability(!showAffordability)}
      >
        <Text style={styles.affordabilityToggleText}>
          Check Loan Affordability
        </Text>
        <Ionicons
          name={showAffordability ? 'chevron-up' : 'chevron-down'}
          size={20}
          color={colors.primary}
        />
      </TouchableOpacity>

      {showAffordability && (
        <View style={styles.affordabilityContainer}>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Monthly income</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput
                style={styles.textInput}
                value={formatNumber(monthlyIncome)}
                onChangeText={handleMonthlyIncomeChange}
                placeholder="50,000"
                keyboardType="numeric"
                placeholderTextColor={colors.textSecondary}
              />
            </View>
          </View>
          {results && (
            <View style={styles.affordabilityResult}>
              <Text style={styles.affordabilityLabel}>EMI to Income Ratio:</Text>
              <Text style={[
                styles.affordabilityValue,
                {
                  color: (results.emi / parseFloat(monthlyIncome || 1)) * 100 <= 40 
                    ? colors.success 
                    : colors.error
                }
              ]}>
                {((results.emi / parseFloat(monthlyIncome || 1)) * 100).toFixed(1)}%
              </Text>
            </View>
          )}
          <Text style={styles.affordabilityNote}>
            Recommended: Keep EMI below 40% of monthly income
          </Text>
        </View>
      )}
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, totalInterest, processingFee } = results;
    const total = principal + totalInterest + processingFee;
    const principalPercentage = (principal / total) * 100;
    const interestPercentage = (totalInterest / total) * 100;
    const feePercentage = (processingFee / total) * 100;

    const radius = 80;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    
    const principalStroke = (principalPercentage / 100) * circumference;
    const interestStroke = (interestPercentage / 100) * circumference;
    const feeStroke = (feePercentage / 100) * circumference;

    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>Cost Breakdown</Text>
        <View style={styles.donutChart}>
          <Svg width={200} height={200}>
            {/* Principal Arc */}
            <Circle
              cx={100}
              cy={100}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${principalStroke} ${circumference}`}
              strokeDashoffset={0}
              strokeLinecap="round"
            />
            {/* Interest Arc */}
            <Circle
              cx={100}
              cy={100}
              r={radius}
              stroke={colors.warning}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${interestStroke} ${circumference}`}
              strokeDashoffset={-principalStroke}
              strokeLinecap="round"
            />
            {/* Processing Fee Arc */}
            <Circle
              cx={100}
              cy={100}
              r={radius}
              stroke={colors.error}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${feeStroke} ${circumference}`}
              strokeDashoffset={-(principalStroke + interestStroke)}
              strokeLinecap="round"
            />
            <SvgText
              x={100}
              y={95}
              textAnchor="middle"
              fontSize={16}
              fontWeight="bold"
              fill={colors.text}
            >
              Total Cost
            </SvgText>
            <SvgText
              x={100}
              y={115}
              textAnchor="middle"
              fontSize={14}
              fill={colors.textSecondary}
            >
              ₹{formatNumber(results.totalCost.toString())}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.legendContainer}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Principal ({principalPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.warning }]} />
            <Text style={styles.legendText}>Interest ({interestPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.error }]} />
            <Text style={styles.legendText}>Processing Fee ({feePercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <Text style={styles.resultsTitle}>Loan Summary</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly EMI</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.emi.toString())}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Principal Amount</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.principal.toString())}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Interest</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.totalInterest.toString())}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Processing Fee</Text>
          <Text style={styles.resultValue}>₹{formatNumber(results.processingFee.toString())}</Text>
        </View>
        
        <View style={[styles.resultItem, styles.totalResultItem]}>
          <Text style={styles.totalResultLabel}>Total Cost</Text>
          <Text style={styles.totalResultValue}>₹{formatNumber(results.totalCost.toString())}</Text>
        </View>
      </View>
    );
  };

  const renderAmortizationSchedule = () => {
    if (!results || !results.breakdown) return null;

    return (
      <View style={styles.scheduleContainer}>
        <Text style={styles.scheduleTitle}>Yearly repayment schedule</Text>
        <Text style={styles.scheduleSubtitle}>
          Year wise break-up of the total payment. This schedule shows the principal and interest component of your loan payment.
        </Text>
        
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Year</Text>
            <Text style={[styles.tableHeaderText, { flex: 2 }]}>Principal (₹)</Text>
            <Text style={[styles.tableHeaderText, { flex: 2 }]}>Interest (₹)</Text>
            <Text style={[styles.tableHeaderText, { flex: 2 }]}>Balance (₹)</Text>
          </View>
          
          {results.breakdown.slice(0, displayedRows).map((row, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCellText, { flex: 1 }]}>{row.year}</Text>
              <Text style={[styles.tableCellText, { flex: 2 }]}>
                {formatNumber(row.principal.toString())}
              </Text>
              <Text style={[styles.tableCellText, { flex: 2 }]}>
                {formatNumber(row.interest.toString())}
              </Text>
              <Text style={[styles.tableCellText, { flex: 2 }]}>
                {formatNumber(row.balance.toString())}
              </Text>
            </View>
          ))}
        </View>
        
        {displayedRows < results.breakdown.length && (
          <TouchableOpacity style={styles.loadMoreButton} onPress={loadMoreRows}>
            <Text style={styles.loadMoreText}>Load More</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderEMIInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>What is Personal Loan EMI Calculator?</Text>
      <Text style={styles.infoText}>
        A Personal Loan EMI Calculator is a financial tool that helps you calculate the Equated Monthly Installment (EMI) 
        for your personal loan. It considers factors like loan amount, interest rate, tenure, and processing fees to give you 
        a complete picture of your loan cost.
      </Text>
      
      <Text style={styles.infoSubtitle}>Key Features</Text>
      <Text style={styles.infoText}>
        • Calculate monthly EMI for personal loans{'\n'}
        • Compare different loan amounts and tenures{'\n'}
        • Include processing fees in total cost calculation{'\n'}
        • Check loan affordability against your income{'\n'}
        • Year-wise amortization schedule{'\n'}
        • Visual breakdown of principal vs interest vs fees
      </Text>
      
      <Text style={styles.infoSubtitle}>How to Use Personal Loan EMI Calculator?</Text>
      <Text style={styles.infoText}>
        1. Enter your desired personal loan amount{'\n'}
        2. Set the interest rate offered by your bank{'\n'}
        3. Choose your preferred loan tenure{'\n'}
        4. Add processing fee (percentage or fixed amount){'\n'}
        5. Optionally check affordability against your income
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Personal Loan EMI Calculator</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Understanding Personal Loans</Text>
        <Text style={styles.seoText}>
          Personal loans are unsecured loans that provide immediate financial assistance for various needs like medical emergencies, 
          home renovation, debt consolidation, or major purchases. Unlike secured loans, personal loans don't require collateral, 
          making them accessible but typically carrying higher interest rates. Our Personal Loan EMI Calculator helps you understand 
          the exact monthly commitment and total cost before applying.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Key Calculator Features</Text>
        <View style={styles.seoFeatureGrid}>
          <View style={styles.seoFeature}>
            <Text style={styles.seoFeatureTitle}>Instant EMI Calculation</Text>
            <Text style={styles.seoFeatureText}>Get accurate monthly installment amounts instantly</Text>
          </View>
          <View style={styles.seoFeature}>
            <Text style={styles.seoFeatureTitle}>Processing Fee Inclusion</Text>
            <Text style={styles.seoFeatureText}>Factor in processing fees for total cost calculation</Text>
          </View>
          <View style={styles.seoFeature}>
            <Text style={styles.seoFeatureTitle}>Affordability Check</Text>
            <Text style={styles.seoFeatureText}>Verify loan affordability against your income</Text>
          </View>
          <View style={styles.seoFeature}>
            <Text style={styles.seoFeatureTitle}>Amortization Schedule</Text>
            <Text style={styles.seoFeatureText}>Detailed year-wise payment breakdown</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Personal Loan vs Other Loan Types</Text>
        <View style={styles.seoComparisonTable}>
          <View style={styles.seoTableHeader}>
            <Text style={styles.seoTableHeaderText}>Loan Type</Text>
            <Text style={styles.seoTableHeaderText}>Interest Rate</Text>
            <Text style={styles.seoTableHeaderText}>Collateral</Text>
            <Text style={styles.seoTableHeaderText}>Processing Time</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Personal Loan</Text>
            <Text style={styles.seoTableCell}>10-24%</Text>
            <Text style={styles.seoTableCell}>Not Required</Text>
            <Text style={styles.seoTableCell}>1-7 days</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Home Loan</Text>
            <Text style={styles.seoTableCell}>7-12%</Text>
            <Text style={styles.seoTableCell}>Property</Text>
            <Text style={styles.seoTableCell}>15-30 days</Text>
          </View>
          <View style={styles.seoTableRow}>
            <Text style={styles.seoTableCell}>Car Loan</Text>
            <Text style={styles.seoTableCell}>8-15%</Text>
            <Text style={styles.seoTableCell}>Vehicle</Text>
            <Text style={styles.seoTableCell}>3-7 days</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>How to Use Personal Loan EMI Calculator</Text>
        <View style={styles.seoSteps}>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>1</Text>
            <Text style={styles.seoStepText}>Enter your desired personal loan amount (₹50,000 to ₹50,00,000)</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>2</Text>
            <Text style={styles.seoStepText}>Set the interest rate offered by your bank or financial institution</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>3</Text>
            <Text style={styles.seoStepText}>Choose your preferred loan tenure (1-7 years typically)</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>4</Text>
            <Text style={styles.seoStepText}>Add processing fee (usually 1-3% of loan amount)</Text>
          </View>
          <View style={styles.seoStep}>
            <Text style={styles.seoStepNumber}>5</Text>
            <Text style={styles.seoStepText}>Check affordability against your monthly income</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Personal Loan Optimization Strategies</Text>
        <View style={styles.seoStrategies}>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Compare Interest Rates</Text>
            <Text style={styles.seoStrategyText}>Shop around different lenders for the best rates. Even 1% difference can save thousands.</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Maintain Good Credit Score</Text>
            <Text style={styles.seoStrategyText}>Credit score above 750 typically gets better interest rates and faster approvals.</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Choose Optimal Tenure</Text>
            <Text style={styles.seoStrategyText}>Shorter tenure means higher EMI but lower total interest. Balance based on your cash flow.</Text>
          </View>
          <View style={styles.seoStrategy}>
            <Text style={styles.seoStrategyTitle}>Prepayment Strategy</Text>
            <Text style={styles.seoStrategyText}>Make partial prepayments when possible to reduce total interest burden.</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Legal and Compliance Considerations</Text>
        <Text style={styles.seoText}>
          Personal loans are regulated by RBI guidelines. Lenders must provide transparent pricing, clear terms, 
          and fair collection practices. Always read the loan agreement carefully, understand all charges, 
          and ensure the lender is RBI-registered. Be aware of your rights regarding prepayment, foreclosure, 
          and grievance redressal mechanisms.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Common Personal Loan Mistakes to Avoid</Text>
        <Text style={styles.seoText}>
          • Borrowing more than needed - Only borrow what's absolutely necessary{'\n'}
          • Ignoring processing fees - Factor in all charges for true cost calculation{'\n'}
          • Not comparing offers - Different lenders offer varying rates and terms{'\n'}
          • Missing EMI payments - Affects credit score and incurs penalty charges{'\n'}
          • Not reading fine print - Understand all terms, conditions, and charges{'\n'}
          • Using for investments - Personal loans are expensive for investment purposes{'\n'}
          • Multiple loan applications - Can negatively impact your credit score
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Personal Loan Success Examples</Text>
        <Text style={styles.seoText}>
          <Text style={styles.seoBold}>Example 1 - Medical Emergency:</Text> Rajesh needed ₹3,00,000 for surgery. 
          With 14% interest for 3 years, his EMI was ₹10,278. Total cost: ₹3,70,008. Quick approval helped during emergency.{'\n\n'}
          
          <Text style={styles.seoBold}>Example 2 - Debt Consolidation:</Text> Priya consolidated ₹5,00,000 credit card debt 
          into personal loan at 12% for 4 years. EMI: ₹13,141. Saved ₹2,00,000 compared to credit card interest.{'\n\n'}
          
          <Text style={styles.seoBold}>Example 3 - Home Renovation:</Text> Kumar borrowed ₹2,00,000 at 11% for 2 years. 
          EMI: ₹9,201. Total cost: ₹2,20,824. Increased home value by ₹4,00,000.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>FAQ</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What is a personal loan EMI?</Text>
              <Text style={styles.faqAnswer}>
                EMI (Equated Monthly Installment) is the fixed amount you pay every month to repay your personal loan. 
                It includes both principal and interest components.
              </Text>
            </View>
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>How is personal loan EMI calculated?</Text>
              <Text style={styles.faqAnswer}>
                Personal loan EMI is calculated using the formula: EMI = P × r × (1+r)^n / ((1+r)^n - 1), 
                where P is principal, r is monthly interest rate, and n is tenure in months.
              </Text>
            </View>
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What factors affect personal loan EMI?</Text>
              <Text style={styles.faqAnswer}>
                Key factors include loan amount, interest rate, loan tenure, and processing fees. 
                Higher loan amounts and interest rates increase EMI, while longer tenure reduces it.
              </Text>
            </View>
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What is the ideal EMI to income ratio?</Text>
              <Text style={styles.faqAnswer}>
                Financial experts recommend keeping your total EMI (including all loans) below 40% of your monthly income 
                to maintain healthy finances and avoid over-leveraging.
              </Text>
            </View>
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>Can I prepay my personal loan?</Text>
              <Text style={styles.faqAnswer}>
                Yes, most banks allow personal loan prepayment. Some may charge prepayment penalties, 
                especially for loans taken at subsidized rates. Check with your lender for specific terms.
              </Text>
            </View>
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What are typical personal loan interest rates?</Text>
              <Text style={styles.faqAnswer}>
                Personal loan interest rates typically range from 10% to 24% per annum, depending on your credit score, 
                income, employment history, and the lender's policies.
              </Text>
            </View>
          </View>
        )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      <View style={styles.fullWidthSection}>
        {renderAmortizationSchedule()}
        {renderEMIInformation()}
        {renderSEOContent()}
        {renderFAQ()}
        <Footer />
      </View>
    </ScrollView>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      paddingHorizontal: 20,
      paddingVertical: 40,
      alignItems: 'center',
    },
    headerContent: {
      width: '100%',
      alignItems: 'center',
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: 'white',
      textAlign: 'center',
      marginBottom: 10,
    },
    headerSubtitle: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
      lineHeight: 24,
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      width: '100%',
      padding: 20,
      gap: 20,
    },
    leftPanel: {
      flex: isMobile ? 1 : 0.6,
    },
    rightPanel: {
      flex: isMobile ? 1 : 0.4,
      gap: 20,
    },
    calculatorContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    sectionTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    inputLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.surface,
      paddingHorizontal: 12,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    textInput: {
      flex: 1,
      height: 48,
      fontSize: 16,
      color: colors.text,
    },
    presetContainer: {
      marginBottom: 20,
    },
    presetLabel: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 8,
    },
    presetButtons: {
      flexDirection: 'row',
      gap: 8,
      flexWrap: 'wrap',
    },
    presetButton: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      backgroundColor: colors.surface,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
    },
    presetButtonText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: '500',
    },
    sliderHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    sliderValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.primary,
    },
    slider: {
      height: 40,
      marginVertical: 8,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    toggleContainer: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
      marginTop: 8,
    },
    toggleButton: {
      flex: 1,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 6,
      alignItems: 'center',
    },
    toggleButtonActive: {
      backgroundColor: colors.primary,
    },
    toggleButtonText: {
      fontSize: 14,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    toggleButtonTextActive: {
      color: 'white',
    },
    processingFeeContainer: {
      gap: 12,
    },
    affordabilityToggle: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 16,
      backgroundColor: colors.surface,
      borderRadius: 8,
      marginTop: 8,
    },
    affordabilityToggleText: {
      fontSize: 16,
      fontWeight: '500',
      color: colors.primary,
    },
    affordabilityContainer: {
      marginTop: 16,
      padding: 16,
      backgroundColor: colors.surface,
      borderRadius: 8,
    },
    affordabilityResult: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 12,
    },
    affordabilityLabel: {
      fontSize: 14,
      color: colors.text,
      fontWeight: '500',
    },
    affordabilityValue: {
      fontSize: 16,
      fontWeight: 'bold',
    },
    affordabilityNote: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 8,
      fontStyle: 'italic',
    },
    chartContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    chartTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    donutChart: {
      marginBottom: 20,
    },
    legendContainer: {
      alignItems: 'flex-start',
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    resultsContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultsTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    totalResultItem: {
      borderBottomWidth: 0,
      borderTopWidth: 2,
      borderTopColor: colors.primary,
      marginTop: 8,
      paddingTop: 16,
    },
    totalResultLabel: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    totalResultValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.primary,
    },
    fullWidthSection: {
      width: '100%',
      paddingHorizontal: 20,
    },
    scheduleContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    scheduleTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    scheduleSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 20,
      lineHeight: 20,
    },
    tableContainer: {
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    tableHeaderText: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 14,
      textAlign: 'center',
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    tableCellText: {
      fontSize: 14,
      color: colors.text,
      textAlign: 'center',
    },
    loadMoreButton: {
      marginTop: 16,
      paddingVertical: 12,
      paddingHorizontal: 24,
      backgroundColor: colors.primary,
      borderRadius: 8,
      alignSelf: 'center',
    },
    loadMoreText: {
      color: 'white',
      fontWeight: '600',
      fontSize: 14,
    },
    infoContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginTop: 20,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    faqContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 24,
    },
    faqTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      paddingHorizontal: 24,
      paddingBottom: 24,
    },
    faqItem: {
      marginBottom: 20,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    seoContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    seoTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 24,
      textAlign: 'center',
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSubtitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    seoBold: {
      fontWeight: 'bold',
      color: colors.text,
    },
    seoFeatureGrid: {
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    seoFeature: {
      flex: isMobile ? 1 : 0.48,
      backgroundColor: colors.surface,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    seoFeatureTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    seoFeatureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    seoComparisonTable: {
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    seoTableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 8,
    },
    seoTableHeaderText: {
      flex: 1,
      color: 'white',
      fontWeight: 'bold',
      fontSize: 12,
      textAlign: 'center',
    },
    seoTableRow: {
      flexDirection: 'row',
      paddingVertical: 10,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    seoTableCell: {
      flex: 1,
      fontSize: 12,
      color: colors.text,
      textAlign: 'center',
    },
    seoSteps: {
      gap: 12,
    },
    seoStep: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    seoStepNumber: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: colors.primary,
      color: 'white',
      fontSize: 14,
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: 24,
    },
    seoStepText: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    seoStrategies: {
      gap: 16,
    },
    seoStrategy: {
      backgroundColor: colors.surface,
      padding: 16,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.success,
    },
    seoStrategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    seoStrategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });
};

export default PersonalLoanEMICalculatorScreen;